import {
  FormControl,
  FormErrorMessage,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Button,
  Select,
  Heading,
  Input,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function EditChip(props) {
  const { isOpen, onClose, chip, organizationsData, setAlert, onChipUpdated } = props;

  const organizations = useMemo(() => organizationsData, [organizationsData]);
  const [departments, setDepartments] = useState([]);

  const authToken = localStorage.getItem("authToken");

  // Form fields (same as NewChip)
  const [nRFId, setnRFId] = useState("");
  const [nRFIdInvalid, setnRFIdInvalid] = useState(false);
  const [chipId, setChipId] = useState("");
  const [chipIdInvalid, setChipIdInvalid] = useState(false);
  const [organization, setOrganization] = useState("");
  const [organizationInvalid, setOrganizationInvalid] = useState(false);
  const [department, setDepartment] = useState("");
  const [departmentInvalid, setDepartmentInvalid] = useState(false);

  // Whether the next dept-fetch should pre-select chip's existing department.
  // Set true when modal opens, cleared after the first fetch resolves so
  // subsequent organization-changes don't overwrite the user's choice.
  const initialDeptSet = useRef(false);

  // Existing nrf_ids for duplicate-check (issue #7). Excludes the chip
  // currently being edited so the user can keep their own nrf_id unchanged.
  const [existingNrfIds, setExistingNrfIds] = useState(new Set());

  useEffect(() => {
    if (!isOpen) return;
    fetch("https://api.beehiveproject.no/chip", {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((chips) => {
        if (!Array.isArray(chips)) return;
        const currentId = chip?._id?.$oid;
        const set = new Set(
          chips
            .filter((c) => c._id?.$oid !== currentId)
            .map((c) => c.nrf_id)
            .filter(Boolean)
        );
        setExistingNrfIds(set);
      })
      .catch((err) =>
        console.error("Failed to fetch chips for duplicate-check:", err)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chip]);

  const trimmedNRFId = (nRFId || "").trim();
  const nRFIdDuplicate =
    trimmedNRFId !== "" && existingNrfIds.has(trimmedNRFId);

  // Initialize form fields when modal opens for a chip
  useEffect(() => {
    if (!isOpen || !chip) return;

    initialDeptSet.current = true;

    setChipId(chip.chip_id || "");
    setnRFId(chip.nrf_id || "");
    setChipIdInvalid(false);
    setnRFIdInvalid(false);
    setOrganizationInvalid(false);
    setDepartmentInvalid(false);

    // Pre-select organization if we have it in the list
    const orgFromList = organizations.find(
      (o) => o._id?.$oid === chip.organization?.$oid
    );
    setOrganization(orgFromList || "");
    setDepartment(""); // will be set by the dept-fetch effect

    // If chip object doesn't include nrf_id (the list endpoint may strip it),
    // fetch the full chip to get it.
    if (!chip.nrf_id) {
      fetch(`https://api.beehiveproject.no/chip/${chip._id.$oid}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.nrf_id) setnRFId(data.nrf_id);
        })
        .catch((err) => console.error("Failed to fetch chip:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chip]);

  // Fetch departments whenever organization changes.
  // Pre-select chip's existing department only on the initial fetch.
  useEffect(() => {
    if (!isOpen) return;
    if (!organization || !organization._id) {
      setDepartments([]);
      return;
    }

    fetch(
      `https://api.beehiveproject.no/organization/${organization._id.$oid}/departments`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    )
      .then((r) => r.json())
      .then((deps) => {
        setDepartments(deps || []);

        if (initialDeptSet.current && chip?.department?.$oid && deps) {
          const dep = deps.find((d) => d._id?.$oid === chip.department.$oid);
          if (dep) setDepartment(dep);
          initialDeptSet.current = false;
        }
      })
      .catch((err) => console.error("Failed to fetch departments:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, isOpen]);

  const saveChip = () => {
    // Validate (same pattern as NewChip)
    const inputs = [nRFId, chipId, organization, department];
    const setInvalids = [
      setnRFIdInvalid,
      setChipIdInvalid,
      setOrganizationInvalid,
      setDepartmentInvalid,
    ];
    let abort = false;
    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i] === "" || inputs[i] === null) {
        setInvalids[i](true);
        abort = true;
      }
    }
    if (abort) return;

    // Block save if nrf_id collides with another chip in the database (issue #7).
    if (nRFIdDuplicate) {
      setAlert([
        "error",
        "Duplikat",
        `nRF ID "${trimmedNRFId}" tilhører allerede en annen chip i databasen.`,
      ]);
      return;
    }

    // Close modal FIRST (NewChip pattern — avoids state-update cascade
    // during Chakra's modal-close animation, which causes the page-freeze
    // we've seen previously).
    onClose();

    const chipIdSnap = chipId;
    const updatedChip = {
      ...chip,
      chip_id: chipId,
      nrf_id: trimmedNRFId,
      organization: {
        $oid: organization._id.$oid,
        name: organization.name,
      },
      department: {
        $oid: department._id.$oid,
        name: department.name,
      },
    };

    const options = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        nrf_id: trimmedNRFId,
        chip_id: chipId,
        organization: organization._id.$oid,
        department: department._id.$oid,
      }),
    };

    fetch(`https://api.beehiveproject.no/chip/${chip._id.$oid}`, options)
      .then((response) => {
        if (response.status === 200) {
          setAlert([
            "success",
            "Suksess",
            `Chip ${chipIdSnap} er oppdatert`,
          ]);
          if (onChipUpdated) onChipUpdated(updatedChip);
        } else if (response.status === 409) {
          setAlert([
            "error",
            "Duplikat",
            `nRF ID "${trimmedNRFId}" tilhører allerede en annen chip.`,
          ]);
        } else {
          setAlert([
            "error",
            "Feil",
            `Kunne ikke oppdatere chip ${chipIdSnap}. nRF ID "${trimmedNRFId}" kan allerede være i bruk, eller serveren svarte med en feil (HTTP ${response.status}).`,
          ]);
        }
      })
      .catch((err) => {
        console.error("Error updating chip:", err);
        setAlert([
          "error",
          "Feil",
          `Nettverksfeil under oppdatering av chip ${chipIdSnap}.`,
        ]);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} returnFocusOnClose={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Rediger Chip</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Heading size="sm">Chip ID</Heading>
            <FormControl isRequired isInvalid={chipIdInvalid}>
              <Input
                value={chipId}
                onChange={(event) => setChipId(event.target.value)}
                placeholder="Chip ID"
              />
              <FormErrorMessage>Chip ID er påkrevd</FormErrorMessage>
            </FormControl>

            <Heading size="sm">nRF ID</Heading>
            <FormControl isRequired isInvalid={nRFIdInvalid || nRFIdDuplicate}>
              <Input
                value={nRFId}
                onChange={(event) => setnRFId(event.target.value)}
                placeholder="nRF ID"
              />
              <FormErrorMessage>
                {nRFIdDuplicate
                  ? `nRF ID "${trimmedNRFId}" tilhører allerede en annen chip`
                  : "nRF ID er påkrevd"}
              </FormErrorMessage>
            </FormControl>

            <Heading size="sm">Organisasjon</Heading>
            <FormControl isRequired isInvalid={organizationInvalid}>
              <Select
                variant="outline"
                value={organization ? JSON.stringify(organization) : ""}
                onChange={(event) => {
                  const data = JSON.parse(event.target.value);
                  setOrganization(data);
                  setDepartment(""); // org change clears dept
                }}
              >
                <option value="" disabled hidden>
                  Velg en organisasjon
                </option>
                {organizations.map((org) => (
                  <option value={JSON.stringify(org)} key={org._id.$oid}>
                    {org.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>Organisasjon er påkrevd</FormErrorMessage>
            </FormControl>

            <Heading size="sm">Avdeling</Heading>
            <FormControl isRequired isInvalid={departmentInvalid}>
              <Select
                variant="outline"
                value={department ? JSON.stringify(department) : ""}
                onChange={(event) => {
                  const data = JSON.parse(event.target.value);
                  setDepartment(data);
                }}
              >
                <option value="" disabled hidden>
                  Velg en avdeling
                </option>
                {departments.map((dep) => (
                  <option value={JSON.stringify(dep)} key={dep._id.$oid}>
                    {dep.icon} {dep.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>Avdeling er påkrevd</FormErrorMessage>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} colorScheme="red">
            Avbryt
          </Button>
          <Spacer />
          <Button onClick={saveChip} colorScheme="brand" isDisabled={nRFIdDuplicate}>
            Lagre
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
